import { Button } from '@/components/ui/primitives';
import {
    MarketingPage,
    Hero,
    ContextNote,
    FeatureGrid,
    StepList,
    FAQList,
    CtaBand,
    PageFooter,
    Dateline,
} from '@/components/marketing';
import { pageForRoute } from '@/site/pageRegistry';
import { MEASURE_HREFLANG_ALTERNATES } from '@/pages/landingContent';

const PAGE = pageForRoute('/measure-pdf-online');

const CHIPS = ['No upload', 'No account', 'Imperial and metric', 'Free'] as const;

// Shipped: dimension tool (DimensionRenderer.tsx), scale calibration (CalibrationOverlay.tsx,
// ScaleDialog.tsx), imperial/metric unit switch (ScaleDialog.tsx),
// area on polygon/rectangle/ellipse (markup-config.ts supportsArea).
// Shipped: scale calibration (CalibrationOverlay.tsx, ScaleDialog.tsx), dimensions
// (markup-renderers/DimensionRenderer.tsx), and supported area markups (core/geometry/markup-config.ts).
const HOW_TO_STEPS = [
    {
        title: 'Calibrate the drawing scale',
        body: 'Choose two points on a labeled dimension or scale bar, enter its real-world length, and apply the scale before relying on measurements.',
        image: '/tool-shots/dimension.jpg',
        alt: 'A calibrated dimension line on a floor plan, showing a real-world measurement',
    },
    {
        title: 'Draw a dimension to measure',
        body: 'Select the dimension tool and mark two endpoints. Read the scaled distance label, then check it against another dimension you know.',
        image: '/tool-shots/dimension.jpg',
        alt: 'A dimension line with extension marks and a measurement on a plan drawing',
    },
    {
        title: 'Measure a region',
        body: 'After calibration, draw a supported polygon or rectangle around a room or zone to see its scaled area.',
        image: '/tool-shots/polygon.jpg',
        alt: 'A polygon markup outlining a room on a floor plan for area measurement',
    },
] as const;

const FEATURE_CARDS = [
    {
        title: 'Dimension lines with witness marks',
        body: 'The architectural dimension tool draws extension and witness lines and labels the real-world distance along the dimension line, matching standard drawing conventions.',
    },
    {
        title: 'Scale calibration from any known length',
        body: 'Click two points on a known feature, enter the real-world distance, and every dimension you draw afterwards reads in the same units at that scale.',
    },
    {
        title: 'Imperial and metric',
        body: 'Set the unit system once for the whole workspace. Switch between feet and inches and metric at any time -- every open dimension label updates immediately.',
    },
    {
        title: 'Area measurement',
        body: 'Draw a polygon or rectangle over a room, zone, or takeoff region and PDF Markup Tool shows the scaled area inside the shape.',
        accent: true,
    },
    {
        title: 'Nothing uploaded',
        body: 'Your PDF stays on your device. Calibration settings and measurement markups are stored locally in your browser -- nothing is sent to a server.',
    },
    {
        title: 'Export with measurements included',
        body: 'When your measurements are complete, export an annotated PDF with markups baked in or save the full markup list to CSV.',
    },
] as const;

const FAQ_ITEMS = [
    {
        question: 'Does measuring a PDF plan require an account?',
        answer: [
            'No. PDF Markup Tool measures distances entirely in your browser with no account and no file upload. Open the tool, load a PDF, calibrate the scale, and start measuring.',
        ],
    },
    {
        question: 'What unit systems are supported?',
        answer: [
            'PDF Markup Tool supports imperial (feet and inches) and metric. You set the unit once for the whole workspace and can switch at any time.',
        ],
    },
    {
        question: 'Can I measure area as well as distance?',
        answer: [
            'Yes. Draw a polygon or rectangle over any region and PDF Markup Tool displays the scaled area inside it. The polygon tool can follow any irregular boundary.',
        ],
    },
    {
        question: 'Does the PDF need to have embedded scale information?',
        answer: [
            'No. You calibrate the scale yourself by clicking two points on a known feature -- such as a door width or a labeled grid line -- and entering its real-world length. PDF Markup Tool uses that calibration to resolve every subsequent dimension.',
        ],
    },
] as const;

export function MeasurePdfOnlinePage() {
    return (
        <MarketingPage head={{ ...PAGE.head, canonical: PAGE.canonical, alternates: MEASURE_HREFLANG_ALTERNATES }}>
            <main>
                <Hero
                    eyebrow="Measure PDF online"
                    title={<>Measure distances on a PDF plan.<br />Free in your browser.</>}
                    lead="Open a floor plan or construction drawing, calibrate to its printed scale, and draw dimension lines to read real-world distances. Your file stays on your device -- nothing is uploaded."
                    cta={<Button href="/" variant="primary" size="lg">Open a PDF plan</Button>}
                    chips={CHIPS}
                />
                <StepList
                    title="How to measure a PDF plan"
                    lead="Calibrate first, check a second known length, then measure distances or supported areas."
                    steps={HOW_TO_STEPS}
                />
                <p className="mktg-container">For rooms and zones, see <a href="/measure-area-pdf/">how to measure area on a PDF floor plan</a>.</p>
                <ContextNote text="PDF Markup Tool runs the full measurement workflow in your browser. No file is sent to a server and no account is needed." />
                <FeatureGrid
                    sectionTitle="What you can measure"
                    sectionLead="Distances, areas, and calibrated takeoffs -- all from a PDF plan in any browser."
                    cards={FEATURE_CARDS}
                />
                <FAQList items={FAQ_ITEMS} />
                <CtaBand
                    title="Open a PDF plan and start measuring"
                    lead="Load any floor plan or construction drawing and take measurements right away -- no signup, no install."
                    actions={<><Button href="/" variant="primary" size="lg">Open a PDF plan</Button><Button href="/redline-pdf-drawing/" variant="ghost" size="lg">How to redline a drawing</Button></>}
                />
                <Dateline reviewed={PAGE.reviewed} />
            </main>
            <PageFooter tagline="Free browser-native PDF measurement for construction drawings" />
        </MarketingPage>
    );
}
